import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Wallet, ArrowDown, ArrowUp, Lock, Info, ChevronRight } from 'lucide-react';

/**
 * VendorWalletCard — iter-230bv
 *
 * Compact dashboard card surfacing the vendor's escrow-wallet balance,
 * status badge (active / hibernated / locked), 5 most-recent ledger
 * entries, and an inline explainer for the RTO debit / pool-credit
 * mechanic. Tap-through to a full ledger view when the user wants
 * more history.
 */
export default function VendorWalletCard({ onViewLedger }) {
  const { apiCall } = useAuth();
  const { formatPrice } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiCall('/api/vendor/wallet');
        if (mounted) setData(res);
      } catch (e) {
        // Wallet is optional surfacing — silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [apiCall]);

  if (loading) {
    return (
      <div
        data-testid="vendor-wallet-card-loading"
        className="rounded-2xl border border-zinc-200 bg-white/70 p-4 animate-pulse"
      >
        <div className="h-4 w-24 bg-zinc-200 rounded mb-3" />
        <div className="h-7 w-32 bg-zinc-200 rounded" />
      </div>
    );
  }
  if (!data?.wallet) return null;

  const w = data.wallet;
  const isNegative = w.balance < 0;
  const isLocked = w.status === 'locked';
  const isHibernated = w.status === 'hibernated';

  const statusMeta = isLocked
    ? { label: 'Locked', cls: 'bg-amber-100 text-amber-800', icon: Lock }
    : isHibernated
    ? { label: 'Hibernated', cls: 'bg-zinc-100 text-zinc-700', icon: Info }
    : isNegative
    ? { label: 'Recovery Mode', cls: 'bg-rose-100 text-rose-800', icon: ArrowDown }
    : { label: 'Active', cls: 'bg-emerald-100 text-emerald-800', icon: Wallet };

  return (
    <div
      data-testid="vendor-wallet-card"
      className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet
            className={`w-5 h-5 ${
              isNegative ? 'text-rose-600' : 'text-emerald-600'
            }`}
          />
          <h3 className="text-sm font-semibold text-zinc-800">
            Shipping Pool Wallet
          </h3>
        </div>
        <span
          data-testid="vendor-wallet-status-badge"
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${statusMeta.cls}`}
        >
          <statusMeta.icon className="w-3 h-3" />
          {statusMeta.label}
        </span>
      </div>

      <div className="mt-3">
        <div
          data-testid="vendor-wallet-balance"
          className={`text-3xl font-bold tracking-tight ${
            isNegative ? 'text-rose-600' : 'text-zinc-900'
          }`}
        >
          {isNegative ? '−' : ''}
          {formatPrice
            ? formatPrice(Math.abs(w.balance))
            : `₹${Math.abs(w.balance).toFixed(2)}`}
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">
          {isNegative
            ? 'Deficit auto-recovers from your next payout.'
            : isLocked
            ? `Will release to cash payout on ${(w.lock_release_at || '').slice(0, 10)}.`
            : isHibernated
            ? 'Wallet is dormant. Switch to Aniyor-managed shipping to reactivate.'
            : 'Credits from shipping-pool surplus. Debits on RTO/returns.'}
        </p>
      </div>

      {/* Lifetime stats */}
      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
        <div className="bg-emerald-50/60 rounded-lg p-2">
          <div className="flex items-center gap-1 text-emerald-700">
            <ArrowUp className="w-3 h-3" />
            Lifetime credits
          </div>
          <div
            data-testid="vendor-wallet-lifetime-credits"
            className="text-zinc-900 font-semibold mt-0.5"
          >
            {formatPrice
              ? formatPrice(w.lifetime_credits || 0)
              : `₹${(w.lifetime_credits || 0).toFixed(2)}`}
          </div>
        </div>
        <div className="bg-rose-50/60 rounded-lg p-2">
          <div className="flex items-center gap-1 text-rose-700">
            <ArrowDown className="w-3 h-3" />
            Lifetime debits
          </div>
          <div
            data-testid="vendor-wallet-lifetime-debits"
            className="text-zinc-900 font-semibold mt-0.5"
          >
            {formatPrice
              ? formatPrice(w.lifetime_debits || 0)
              : `₹${(w.lifetime_debits || 0).toFixed(2)}`}
          </div>
        </div>
      </div>

      {/* Recent activity (top 5) */}
      {Array.isArray(data.recent_ledger) && data.recent_ledger.length > 0 ? (
        <div className="mt-4">
          <div className="text-xs font-medium text-zinc-700 mb-2">
            Recent activity
          </div>
          <ul className="space-y-1.5">
            {data.recent_ledger.slice(0, 5).map((row) => {
              const isCredit =
                row.txn_type === 'credit' ||
                row.txn_type === 'payout_recovery' ||
                row.txn_type === 'activate';
              return (
                <li
                  key={row.id}
                  data-testid="vendor-wallet-ledger-row"
                  className="flex items-start justify-between gap-2 text-[11px] py-1 border-b border-zinc-100 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-800 truncate">{row.note || row.txn_type}</div>
                    <div className="text-zinc-400 text-[10px] mt-0.5">
                      {(row.created_at || '').slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                  <div
                    className={`text-right font-semibold tabular-nums ${
                      isCredit ? 'text-emerald-600' : 'text-rose-600'
                    }`}
                  >
                    {isCredit ? '+' : '−'}
                    {formatPrice
                      ? formatPrice(row.amount)
                      : `₹${(row.amount || 0).toFixed(2)}`}
                  </div>
                </li>
              );
            })}
          </ul>
          {onViewLedger ? (
            <button
              data-testid="vendor-wallet-view-full-ledger"
              type="button"
              onClick={onViewLedger}
              className="mt-3 w-full text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center justify-center gap-1"
            >
              View full ledger
              <ChevronRight className="w-3 h-3" />
            </button>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 text-[11px] text-zinc-500 bg-zinc-50 rounded-lg p-2.5">
          No wallet activity yet. Surpluses from each delivered order
          will appear here.
        </div>
      )}

      {/* Inline explainer */}
      <button
        data-testid="vendor-wallet-info-toggle"
        type="button"
        onClick={() => setShowInfo((s) => !s)}
        className="mt-3 text-[11px] text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
      >
        <Info className="w-3 h-3" />
        {showInfo ? 'Hide' : 'How does this work?'}
      </button>
      {showInfo ? (
        <div
          data-testid="vendor-wallet-info-panel"
          className="mt-2 text-[11px] leading-relaxed text-zinc-600 bg-zinc-50 rounded-lg p-3 space-y-1.5"
        >
          <p>
            <strong>Credits:</strong> On each Aniyor-managed delivery, the 5%
            shipping-pool you contribute is partially used to subsidise the
            customer&apos;s shipping fee. The unused portion lands here.
          </p>
          <p>
            <strong>Debits:</strong> On RTO/return, the reverse-shipping
            charge is deducted from this wallet so your future payouts
            stay clean.
          </p>
          <p>
            <strong>Negative balance:</strong> Recovers automatically from
            your next payout. You will not be asked for any out-of-pocket
            settlement.
          </p>
          <p>
            <strong>Self-managed shipping:</strong> Wallet pauses
            (hibernates). You handle returns via your own courier.
          </p>
        </div>
      ) : null}
    </div>
  );
}
